import { useEffect, useRef, useState } from 'react'
import { Box, Field, HStack, Slider, Spinner, Text } from '@chakra-ui/react'
import { useTranslation } from '../../i18n'
import { thumbnailAt, reelDurationOf } from '../../utils/thumbnails'
import { isStreamReady } from '../../utils/streamCopy'

/**
 * Where Cloudflare puts a reel's poster when nobody has chosen a frame. It is
 * the `thumbnailTimestampPct` sent with the copy request in
 * `functions/lib/cloudflare.js` — 10% in, not the first frame. This is only
 * used to park the slider handle where the current poster actually is; the
 * reel keeps no `thumbTime` until someone moves it.
 */
const DEFAULT_THUMB_PCT = 0.1

/** Long enough that a drag is one request, short enough to feel live. */
const PREVIEW_DEBOUNCE_MS = 250

/**
 * How far short of the end the slider stops. Cloudflare refuses a `time` at or
 * past the video's duration — "thumbnail timestamp exceeds duration of video",
 * a 400 with no image — and its idea of the duration can differ from ours in
 * the last fraction of a second. A tenth of a second of unreachable tail is
 * cheaper than a poster that silently fails to load.
 */
const END_MARGIN_S = 0.1

/** `0:14.5`, dropping the tenths when there are none. */
function formatTimecode(seconds) {
    const whole = Math.floor(seconds)
    const tenths = Math.round((seconds - whole) * 10)
    const mm = Math.floor(whole / 60)
    const ss = String(whole % 60).padStart(2, '0')
    return `${mm}:${ss}${tenths ? `.${tenths}` : ''}`
}

/**
 * Pick the frame Cloudflare serves as a reel's poster.
 *
 * The preview is the published URL itself — the same bytes the gallery and the
 * share page will show — rather than a resized copy, so what is approved here
 * is exactly what ships, and the two requests share a cache entry. Cloudflare
 * renders any frame of an encoded video on demand, so nothing here re-encodes
 * and nothing here calls the Stream API.
 *
 * @param {object} reel The reel being edited.
 * @param {?number} value The chosen frame in seconds, or null for none.
 * @param {function(number)} onChange Called with the new frame as it moves.
 * @param {boolean} disabled Locked while the dialog is saving.
 */
export function ThumbnailPicker({ reel, value, onChange, disabled }) {
    const { t } = useTranslation()

    const duration = reelDurationOf(reel)
    const base = reel?.cf?.thumbnailUrl || null
    const ready = isStreamReady(reel) && !!base && !!duration

    const maxSeconds = duration ? Math.max(0, duration - END_MARGIN_S) : 0

    // Null means nobody has chosen, so the handle sits over the frame
    // Cloudflare is already serving. It stays null in the parent until the
    // user actually drags — parking the handle is not a choice. Clamped, so a
    // value stored against a longer earlier encode cannot ask for a frame that
    // no longer exists.
    const position = Math.min(value ?? (duration ? duration * DEFAULT_THUMB_PCT : 0), maxSeconds)

    const [preview, setPreview] = useState(position)
    const [loading, setLoading] = useState(true)
    const [failed, setFailed] = useState(false)

    // What the <img> is currently pointed at. A ref rather than a comparison
    // against `preview`, because a drag that ends back where it started must
    // not raise the spinner over an image that will never fire `load` again.
    const shownRef = useRef(position)

    useEffect(() => {
        if (!ready) return undefined
        const id = setTimeout(() => {
            if (shownRef.current === position) return
            shownRef.current = position
            setLoading(true)
            setFailed(false)
            setPreview(position)
        }, PREVIEW_DEBOUNCE_MS)
        return () => clearTimeout(id)
    }, [position, ready])

    // The previous frame stays on screen behind the spinner while the next one
    // loads, so dragging reads as a slow scrub rather than a series of blanks.
    const src = ready ? thumbnailAt(base, preview) : null

    return (
        <Field.Root>
            <Field.Label>{t('reels.thumbnail')}</Field.Label>

            {!ready ? (
                <Field.HelperText>{t('reels.thumbnailPending')}</Field.HelperText>
            ) : (
                <>
                    <Box
                        position="relative"
                        w="100%"
                        aspectRatio="16 / 9"
                        bg="bg.muted"
                        borderRadius="md"
                        overflow="hidden"
                    >
                        <img
                            src={src}
                            alt=""
                            onLoad={() => setLoading(false)}
                            onError={() => { setLoading(false); setFailed(true) }}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                display: failed ? 'none' : 'block',
                            }}
                        />
                        {loading && (
                            <Box position="absolute" inset={0} display="grid" placeItems="center">
                                <Spinner size="sm" />
                            </Box>
                        )}
                        {failed && (
                            <Box position="absolute" inset={0} display="grid" placeItems="center">
                                <Text fontSize="xs" color="fg.muted">
                                    {t('reels.thumbnailFailed')}
                                </Text>
                            </Box>
                        )}
                    </Box>

                    <Slider.Root
                        mt={3}
                        w="100%"
                        min={0}
                        max={maxSeconds}
                        step={0.1}
                        disabled={disabled}
                        value={[position]}
                        onValueChange={(e) => onChange(e.value[0])}
                    >
                        <Slider.Control>
                            <Slider.Track>
                                <Slider.Range />
                            </Slider.Track>
                            <Slider.Thumbs />
                        </Slider.Control>
                    </Slider.Root>

                    <HStack justify="space-between" w="100%" mt={1}>
                        <Text fontSize="xs" color="fg.muted">{formatTimecode(position)}</Text>
                        <Text fontSize="xs" color="fg.muted">{formatTimecode(duration)}</Text>
                    </HStack>

                    <Field.HelperText>{t('reels.thumbnailHelp')}</Field.HelperText>
                </>
            )}
        </Field.Root>
    )
}
